import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Award, RotateCcw } from 'lucide-react'
import type { Cursus } from '../lib/types'
import type { Voortgang } from '../../gedeeld/types'
import Blokken, { isWerkvorm } from './Blokken'
import { tekstVanModule } from '../lib/voorlezen'
import Suggestie from './Suggestie'
import Voorlezen from './Voorlezen'
import Toets from './Toets'

/* ===========================================================================
 *  Eén cursus doorlopen
 *
 *  Een module per scherm, en aan het eind de toets. Geen zijbalk met alle
 *  modules onder elkaar: op een telefoon is dat een lijst waar je langs moet
 *  scrollen voordat je bij de tekst bent, en op een tablet is het een tweede
 *  menu naast het menu.
 *
 *  In plaats daarvan de stippen bovenaan. Ze zeggen waar je bent, hoeveel er
 *  nog komt, en je kunt erop tikken om terug te springen naar iets wat je al
 *  gelezen hebt. Vooruitspringen kan niet -- niet om streng te doen, maar
 *  omdat module 4 op module 3 leunt en je anders begint met het antwoord op
 *  een vraag die je nog niet gesteld hebt.
 * =========================================================================== */

type Stap = number | 'toets'

export default function CursusScherm({
  cursus, voortgang, naam, opModuleAf, opToets, opOpnieuw, opTerug,
}: {
  cursus: Cursus
  voortgang: Voortgang
  naam: string
  opModuleAf: (moduleId: string) => void
  opToets: (score: number, geslaagd: boolean) => void
  /** De hele cursus opnieuw: gelezen modules weg, score en diploma blijven. */
  opOpnieuw: () => void
  opTerug: () => void
}) {
  /*
   * Waar begin je?
   *
   * Bij de eerste module die nog niet af is. Wie de helft heeft gedaan en
   * terugkomt, wil verder -- niet opnieuw beginnen bij module 1 en zelf
   * zoeken waar hij gebleven was. Is alles af, dan begin je bij het begin;
   * dan ben je hier om terug te lezen.
   */
  const [stap, setStap] = useState<Stap>(() => {
    const eerste = cursus.modules.findIndex((m) => !voortgang.lessen.includes(m.id))
    if (eerste === -1) return voortgang.geslaagdAt ? 0 : 'toets'
    return eerste
  })

  /* Welke werkvormen in deze module zijn gedaan. Leeg bij elke moduleswitch:
     terugkomen op een module betekent hem opnieuw doen, en dat is geen straf
     maar precies waarvoor je terugkwam. */
  const [gedaan, setGedaan] = useState<Set<number>>(new Set())

  const module = typeof stap === 'number' ? cursus.modules[stap] : null

  const werkvormen = useMemo(
    () => (module ? module.blokken.filter(isWerkvorm).length : 0),
    [module])

  const alleGedaan = gedaan.size >= werkvormen

  /* Bovenaan beginnen bij elke stap. Zonder dit sta je na "volgende" midden
     in de nieuwe module, op de hoogte waar je stond te lezen. */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setGedaan(new Set())
  }, [stap])

  function verder() {
    if (typeof stap !== 'number' || !module) return
    opModuleAf(module.id)
    setStap(stap + 1 < cursus.modules.length ? stap + 1 : 'toets')
  }

  function terug() {
    if (stap === 'toets') { setStap(cursus.modules.length - 1); return }
    if (stap > 0) { setStap(stap - 1); return }
    opTerug()
  }

  const gelezen = cursus.modules.map((m) => voortgang.lessen.includes(m.id))

  return (
    <>
      <main className={`les accent-${cursus.accent}`}>
        <div className="midden">
          <div className="les-kop">
            <div className="les-balk">
              <button className="terug" onClick={opTerug}>
                <ArrowLeft size={15} /> Alle cursussen
              </button>

              {/*
                * Opnieuw beginnen, en alleen als er iets te herbeginnen is.
                * Bij een cursus waar je nog niet aan begonnen bent, is het een
                * knop die niets doet -- en zo'n knop leert mensen dat knoppen
                * hier niets doen.
                */}
              <span className="les-knoppen">
                {/* Alleen als het toestel een Nederlandse stem heeft; zie
                    lib/voorlezen.ts. */}
                {module && <Voorlezen tekst={tekstVanModule(module)} />}

                {(voortgang.lessen.length > 0 || voortgang.geslaagdAt) && (
                  <button
                    className="knop kaal klein"
                    onClick={() => { opOpnieuw(); setStap(0) }}
                    title="Zet je voortgang terug naar module 1. Je score en je diploma blijven staan."
                  >
                    <RotateCcw size={14} /> Opnieuw doen
                  </button>
                )}
              </span>
            </div>

            {/* De banenrail. Terug mag, vooruit niet; zie de kop. */}
            <nav className="baanrail" aria-label="Modules">
              {cursus.modules.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  className={`baanvak ${stap === i ? 'hier' : ''} ${gelezen[i] ? 'gedaan' : ''}`}
                  title={`${i + 1}. ${m.titel}`}
                  aria-label={`${i + 1}. ${m.titel}`}
                  aria-current={stap === i ? 'step' : undefined}
                  disabled={typeof stap === 'number' ? i > stap : false}
                  onClick={() => setStap(i)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                className={`baanvak slot ${stap === 'toets' ? 'hier' : ''} ${voortgang.geslaagdAt ? 'gedaan' : ''}`}
                title="Toets"
                aria-label="Toets"
                aria-current={stap === 'toets' ? 'step' : undefined}
                disabled={stap !== 'toets' && !gelezen[cursus.modules.length - 1]}
                onClick={() => setStap('toets')}
              >
                <Award size={16} />
              </button>
            </nav>

            <div key={String(stap)} className="opkomst">
                <div className="stap">
                  {module
                    ? `Module ${(stap as number) + 1} van ${cursus.modules.length}`
                    : 'Afsluitende toets'}
                </div>
                <h1>{module ? module.titel : cursus.titel}</h1>
                <p className="kop">
                  {module
                    ? module.kop
                    : `Tien vragen over wat je net gelezen hebt. Je hebt ${cursus.slaagpercentage}% nodig.`}
              </p>
            </div>
          </div>

          <div key={`inhoud-${String(stap)}`}>
            {module ? (
                <>
                  <Blokken
                    blokken={module.blokken}
                    opKlaar={(i) => setGedaan((was) => new Set(was).add(i))}
                  />
                  <Suggestie
                    cursus={cursus.id}
                    cursusNaam={cursus.titel}
                    module={module.titel}
                  />
                </>
            ) : (
              <div className="blokken">
                <Toets
                  cursus={cursus}
                  eerderGeslaagd={!!voortgang.geslaagdAt}
                  naam={naam}
                  opKlaar={opToets}
                  opNaarOverzicht={opTerug}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* De balk onderaan hoort niet bij de toets: die heeft zijn eigen
          knoppen, en twee "volgende"-knoppen op één scherm is er één te veel. */}
      {module && (
        <div className="voetbalk">
          <div className="voetbalk-in">
            <button className="knop kaal" onClick={terug}>
              <ArrowLeft size={16} /> Terug
            </button>

            {/*
              * De lamp boven de baan.
              *
              * Amber zolang er nog een opdracht open staat, groen zodra je
              * door mag -- en met het woord erbij, want wie kleuren slecht
              * onderscheidt heeft aan een lampje niets. Dat is in deze
              * bedrijfstak geen zeldzaamheid.
              */}
            <span className="tel">
              {werkvormen > 0 && !alleGedaan ? (
                <span className="lamp wacht">
                  <span className="bol" />
                  Opdracht{werkvormen > 1 ? 'en' : ''} afmaken
                  {werkvormen > 1 && ` · ${gedaan.size}/${werkvormen}`}
                </span>
              ) : (
                <span className="lamp door">
                  <span className="bol" />
                  Je kunt door
                </span>
              )}
            </span>

            <button className="knop hoofd" onClick={verder} disabled={!alleGedaan}>
              {(stap as number) + 1 < cursus.modules.length ? 'Volgende module' : 'Naar de toets'}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
